const SIZE = 48;

/* ── colour helpers ───────────────────────────────────────────────── */

function hexToRgb(hex) {
  const clean = String(hex || '#888').replace('#', '');
  const normalized = clean.length === 3
    ? clean.split('').map((x) => x + x).join('')
    : clean.padEnd(6, '8').slice(0, 6);
  const n = Number.parseInt(normalized, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex({ r, g, b }) {
  const h = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function tint(hex, amt) {
  const c = hexToRgb(hex);
  return rgbToHex({ r: c.r + amt, g: c.g + amt, b: c.b + amt });
}

function mix(a, b, r = 0.5) {
  const ca = hexToRgb(a), cb = hexToRgb(b);
  return rgbToHex({ r: ca.r + (cb.r - ca.r) * r, g: ca.g + (cb.g - ca.g) * r, b: ca.b + (cb.b - ca.b) * r });
}

/* ── canvas primitives ────────────────────────────────────────────── */

function fill(ctx, x, y, w, h, c) { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); }

function poly(ctx, pts, c) {
  if (!pts || pts.length < 3) return;
  ctx.fillStyle = c;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
  ctx.fill();
}

function clr(ctx) { ctx.clearRect(0, 0, SIZE, SIZE); }

function shadow(ctx, x, y, w, h, a = 0.25) { fill(ctx, x + 1, y + 1, w, h, `rgba(0,0,0,${a})`); }

/* ── tier palette system ──────────────────────────────────────────── */
// Each tier has a full set of Minecraft-faithful colors

const TIER = {
  wood:    { border: '#7B5B28', head: '#B88B50', hi: '#D4A864', lo: '#8B6328', accent: '#D4A864', handle: '#9F6A3A' },
  stone:   { border: '#7A7A7A', head: '#9A9A9A', hi: '#BCBCBC', lo: '#686868', accent: '#B0B0B0', handle: '#9F6A3A' },
  iron:    { border: '#A0A0A0', head: '#D8D8D8', hi: '#F0F0F0', lo: '#9A9A9A', accent: '#E8E8E8', handle: '#8B6328' },
  gold:    { border: '#C8960A', head: '#FDD835', hi: '#FFE87A', lo: '#C49000', accent: '#FFE150', handle: '#8B6328' },
  diamond: { border: '#00909E', head: '#4AEDD9', hi: '#A0FFF6', lo: '#2BA8A8', accent: '#7DFFF0', handle: '#8B6328' },
  leather: { border: '#704020', head: '#C06A30', hi: '#D88848', lo: '#8B4513', accent: '#D47838', handle: '#9F6A3A' },
};

// Backward-compatible border map
const TIER_BORDER = Object.fromEntries(Object.entries(TIER).map(([k, v]) => [k, v.border]));

/* ── frame (2px border + dark background) ────────────────────────── */

function frame(ctx, borderColor) {
  const bc = borderColor || '#5a5e68';
  const bcLight = tint(bc, 40);
  const bcDark = tint(bc, -32);
  // outer border (2px) – light top-left, dark bottom-right
  fill(ctx, 0, 0, 48, 48, bcDark);
  fill(ctx, 0, 0, 48, 2, bcLight);
  fill(ctx, 0, 0, 2, 48, bcLight);
  fill(ctx, 46, 0, 2, 48, bcDark);
  fill(ctx, 0, 46, 48, 2, bcDark);
  // very dark inner background for maximum item contrast
  fill(ctx, 2, 2, 44, 44, '#1a1d26');
  // subtle warm inner bevel
  fill(ctx, 2, 2, 44, 1, 'rgba(255,255,255,0.08)');
  fill(ctx, 2, 2, 1, 44, 'rgba(255,255,255,0.05)');
}

/* ── 2D flat block face ───────────────────────────────────────────── */

function blockFace(ctx, baseColor) {
  const r = { x: 5, y: 5, w: 38, h: 38 };
  const light = tint(baseColor, 26);
  const dark = tint(baseColor, -28);
  fill(ctx, r.x, r.y, r.w, r.h, baseColor);
  fill(ctx, r.x, r.y, r.w, 2, light);
  fill(ctx, r.x, r.y, 2, r.h, light);
  fill(ctx, r.x + r.w - 2, r.y, 2, r.h, dark);
  fill(ctx, r.x, r.y + r.h - 2, r.w, 2, dark);
  return r;
}

/* ── texture pattern helpers ──────────────────────────────────────── */

function noiseGrid(ctx, r, colors, sp = 5, ins = 3) {
  const sw = colors.length ? colors : ['#888'];
  let ci = 0;
  for (let y = r.y + ins; y < r.y + r.h - ins; y += sp)
    for (let x = r.x + ins; x < r.x + r.w - ins; x += sp) {
      fill(ctx, x, y, 3, 3, sw[ci % sw.length]);
      ci++;
    }
}

function brickPat(ctx, r, mortar, brick) {
  fill(ctx, r.x + 2, r.y + 2, r.w - 4, r.h - 4, brick);
  for (let y = r.y + 6; y < r.y + r.h - 3; y += 7) fill(ctx, r.x + 2, y, r.w - 4, 1, mortar);
  let row = 0;
  for (let y = r.y + 2; y < r.y + r.h - 3; y += 7) {
    for (let x = r.x + 6 + (row % 2 === 0 ? 0 : 6); x < r.x + r.w - 3; x += 11)
      fill(ctx, x, y, 1, 6, mortar);
    row++;
  }
}

function bands(ctx, r, colors, h = 4) {
  const sw = colors.length ? colors : ['#888'];
  let i = 0;
  for (let y = r.y + 2; y < r.y + r.h - 2; y += h) {
    fill(ctx, r.x + 2, y, r.w - 4, Math.min(h, r.y + r.h - 2 - y), sw[i % sw.length]);
    i++;
  }
}

function vStreaks(ctx, r, colors, w = 4) {
  const sw = colors.length ? colors : ['#888'];
  let i = 0;
  for (let x = r.x + 2; x < r.x + r.w - 2; x += w + 1) {
    fill(ctx, x, r.y + 2, w, r.h - 4, sw[i % sw.length]);
    i++;
  }
}

function oreChunks(ctx, r, stoneBase, oreColor) {
  noiseGrid(ctx, r, [tint(stoneBase, 16), tint(stoneBase, -14), tint(stoneBase, 8)], 5, 3);
  const bright = tint(oreColor, 30);
  const dk = tint(oreColor, -18);
  const patches = [
    [r.x + 5, r.y + 6, 7, 5],
    [r.x + 17, r.y + 5, 6, 6],
    [r.x + 8, r.y + 17, 8, 5],
    [r.x + 22, r.y + 18, 6, 6],
    [r.x + 26, r.y + 8, 5, 5]
  ];
  for (const [px, py, pw, ph] of patches) {
    fill(ctx, px, py, pw, ph, dk);
    fill(ctx, px + 1, py + 1, pw - 1, ph - 1, oreColor);
    fill(ctx, px + 1, py + 1, pw - 2, 1, bright);
  }
}

/* ── 2D block variant rendering ───────────────────────────────────── */

function drawBlockVariant(ctx, key, baseColor) {
  frame(ctx, baseColor);
  const f = blockFace(ctx, baseColor || '#8a8a8a');

  switch (key) {
    case 'grass':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, 10, '#69b24f');
      fill(ctx, f.x + 2, f.y + 12, f.w - 4, f.h - 14, '#8d6338');
      noiseGrid(ctx, { x: f.x, y: f.y + 12, w: f.w, h: f.h - 12 }, ['#95683a', '#7d572f', '#ab7b48'], 5, 3);
      fill(ctx, f.x + 5, f.y + 9, 5, 4, '#84cf6f');
      fill(ctx, f.x + 16, f.y + 9, 7, 3, '#4e8f38');
      fill(ctx, f.x + 28, f.y + 10, 4, 3, '#7cc862');
      break;
    case 'dirt':
      noiseGrid(ctx, f, ['#9a6a3b', '#7a522d', '#b6814f', '#a07040'], 5, 3);
      break;
    case 'log': {
      const bc = baseColor || '#6a4525';
      vStreaks(ctx, f, [tint(bc, 14), tint(bc, -12), tint(bc, 24), tint(bc, -6)], 4);
      // cross-section ring
      fill(ctx, f.x + 10, f.y + 6, 12, 12, tint(bc, 48));
      fill(ctx, f.x + 13, f.y + 9, 6, 6, tint(bc, 28));
      fill(ctx, f.x + 15, f.y + 11, 2, 2, tint(bc, 8));
      break;
    }
    case 'planks': {
      const bc = baseColor || '#cf9d62';
      bands(ctx, f, [tint(bc, 12), tint(bc, -12), tint(bc, 6), tint(bc, -8), tint(bc, 4)], 5);
      for (let y = f.y + 4; y < f.y + f.h - 3; y += 5)
        fill(ctx, f.x + 3, y, f.w - 6, 1, 'rgba(70,41,18,0.22)');
      break;
    }
    case 'leaves':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#2e7b31');
      noiseGrid(ctx, f, ['#52a34a', '#2d6d30', '#7bc16c', '#44943e', '#60b855'], 5, 3);
      fill(ctx, f.x + 7, f.y + 7, 4, 4, '#173f1d');
      fill(ctx, f.x + 22, f.y + 18, 4, 4, '#173f1d');
      fill(ctx, f.x + 14, f.y + 26, 3, 3, '#1e4f22');
      break;
    case 'stone':
      noiseGrid(ctx, f, ['#a0a4ab', '#767b82', '#8b9097', '#959aa0', '#848990'], 5, 3);
      fill(ctx, f.x + 8, f.y + 14, 12, 1, '#6a6f76');
      fill(ctx, f.x + 20, f.y + 24, 8, 1, '#6a6f76');
      break;
    case 'cobble':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#76797f');
      for (const [x, y, w, h, c] of [
        [f.x + 4, f.y + 4, 8, 7, '#8b9096'],
        [f.x + 14, f.y + 5, 9, 7, '#676b72'],
        [f.x + 26, f.y + 4, 7, 8, '#959aa0'],
        [f.x + 5, f.y + 14, 8, 8, '#969ba1'],
        [f.x + 16, f.y + 15, 8, 9, '#5d6167'],
        [f.x + 27, f.y + 15, 7, 7, '#838890'],
        [f.x + 8, f.y + 25, 10, 6, '#8e9298'],
        [f.x + 22, f.y + 25, 8, 7, '#6e7278']
      ]) fill(ctx, x, y, w, h, c);
      break;
    case 'stone_bricks':
      brickPat(ctx, f, '#61656b', '#91969d');
      break;
    case 'smooth_stone':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#9da2aa');
      fill(ctx, f.x + 3, f.y + 8, f.w - 6, 2, '#b5bac2');
      fill(ctx, f.x + 5, f.y + 20, f.w - 10, 2, '#7f848c');
      fill(ctx, f.x + 3, f.y + 30, f.w - 6, 1, '#b0b5be');
      break;
    case 'sand':
      noiseGrid(ctx, f, ['#e3d196', '#cdb56d', '#f0e0af', '#d8c580'], 5, 3);
      break;
    case 'sandstone':
      bands(ctx, f, ['#e5d5a6', '#c7af6d', '#dbc58e', '#c2a465'], 6);
      fill(ctx, f.x + 5, f.y + 12, f.w - 10, 2, '#ad9152');
      fill(ctx, f.x + 5, f.y + 24, f.w - 10, 2, '#b89c5e');
      break;
    case 'gravel':
      noiseGrid(ctx, f, ['#9b9ca2', '#7d7f86', '#b4b5ba', '#6c6e75', '#a2a4aa'], 5, 3);
      break;
    case 'obsidian':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#2d2541');
      noiseGrid(ctx, f, ['#3f315e', '#1f1a2d', '#5b4f7c', '#352a50'], 6, 3);
      fill(ctx, f.x + 10, f.y + 8, 3, 8, '#6854a0');
      fill(ctx, f.x + 24, f.y + 20, 4, 6, '#5a4890');
      break;
    case 'ore':
      oreChunks(ctx, f, '#8a8d92', baseColor);
      break;
    case 'crafting_table':
      bands(ctx, f, ['#b98b56', '#9d7142', '#b17f4b'], 5);
      fill(ctx, f.x + 6, f.y + 6, 16, 16, '#6a4525');
      fill(ctx, f.x + 8, f.y + 8, 12, 12, '#c49c6d');
      fill(ctx, f.x + 13, f.y + 8, 2, 12, '#7c5935');
      fill(ctx, f.x + 8, f.y + 13, 12, 2, '#7c5935');
      break;
    case 'furnace':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#7d8188');
      fill(ctx, f.x + 7, f.y + 5, 14, 9, '#9ca2aa');
      fill(ctx, f.x + 7, f.y + 19, 14, 10, '#2d3138');
      fill(ctx, f.x + 9, f.y + 21, 10, 6, '#111418');
      fill(ctx, f.x + 11, f.y + 23, 6, 3, '#e6752a');
      fill(ctx, f.x + 23, f.y + 7, 3, 18, '#585d64');
      break;
    case 'chest':
      fill(ctx, f.x + 2, f.y + 4, f.w - 4, f.h - 6, '#ad7b3e');
      fill(ctx, f.x + 3, f.y + 7, f.w - 6, 3, '#c99858');
      fill(ctx, f.x + 3, f.y + 17, f.w - 6, 2, '#7d5628');
      fill(ctx, f.x + 12, f.y + 14, 7, 8, '#d8bb6d');
      fill(ctx, f.x + 14, f.y + 17, 3, 4, '#7d5628');
      break;
    case 'bed':
      fill(ctx, f.x + 2, f.y + 4, f.w - 4, 12, '#bf4545');
      fill(ctx, f.x + 2, f.y + 16, f.w - 4, 12, '#ece7df');
      fill(ctx, f.x + 5, f.y + 28, 4, 3, '#7d4a2a');
      fill(ctx, f.x + 22, f.y + 28, 4, 3, '#7d4a2a');
      break;
    case 'door':
      fill(ctx, f.x + 8, f.y + 2, 14, f.h - 4, '#b3834d');
      fill(ctx, f.x + 10, f.y + 5, 10, f.h - 10, '#916438');
      fill(ctx, f.x + 18, f.y + 14, 2, 3, '#e6d09d');
      break;
    case 'trapdoor':
      fill(ctx, f.x + 2, f.y + 6, f.w - 4, 16, '#9d6c39');
      fill(ctx, f.x + 5, f.y + 10, f.w - 10, 8, '#6a4525');
      fill(ctx, f.x + 14, f.y + 6, 2, 16, '#be8d54');
      break;
    case 'fence':
      fill(ctx, f.x + 7, f.y + 4, 4, f.h - 8, '#9f6d38');
      fill(ctx, f.x + 20, f.y + 4, 4, f.h - 8, '#9f6d38');
      fill(ctx, f.x + 5, f.y + 10, 22, 3, '#c39255');
      fill(ctx, f.x + 5, f.y + 20, 22, 3, '#86572c');
      break;
    case 'glass':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, 'rgba(175,225,247,0.55)');
      fill(ctx, f.x + 3, f.y + 3, f.w - 6, 2, 'rgba(255,255,255,0.55)');
      fill(ctx, f.x + 7, f.y + 6, 2, f.h - 12, 'rgba(255,255,255,0.4)');
      fill(ctx, f.x + 18, f.y + 8, 2, f.h - 14, 'rgba(255,255,255,0.35)');
      fill(ctx, f.x + 5, f.y + 18, f.w - 10, 2, 'rgba(255,255,255,0.35)');
      break;
    case 'concrete':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, tint(baseColor, 2));
      fill(ctx, f.x + 4, f.y + 6, f.w - 8, 2, tint(baseColor, 18));
      fill(ctx, f.x + 4, f.y + f.h - 8, f.w - 8, 2, tint(baseColor, -16));
      break;
    case 'asphalt':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#2e3137');
      noiseGrid(ctx, f, ['#43464d', '#24272b', '#5a5e65'], 6, 3);
      break;
    case 'glowstone':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#c6883a');
      for (const [x, y] of [[5, 5], [18, 7], [9, 18], [22, 20], [30, 10]])
        fill(ctx, f.x + x, f.y + y, 5, 5, '#ffd487');
      break;
    case 'quartz':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#f0ebe4');
      bands(ctx, f, ['#f5f0ea', '#ddd7cf', '#ebe5de'], 5);
      break;
    case 'netherrack':
      noiseGrid(ctx, f, ['#8b3b31', '#6f2921', '#b5554a', '#7a3028'], 5, 3);
      break;
    case 'soul_sand':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#6a5341');
      noiseGrid(ctx, f, ['#80634e', '#503d30', '#96735c'], 5, 3);
      for (const [x, y] of [[6, 7], [20, 9], [10, 22]]) fill(ctx, f.x + x, f.y + y, 4, 4, '#39281f');
      break;
    case 'nether_bricks':
      brickPat(ctx, f, '#231216', '#52292f');
      break;
    case 'end_stone':
      noiseGrid(ctx, f, ['#ddd9ad', '#bfb987', '#efeabf', '#d0cc9e'], 5, 3);
      break;
    case 'purpur':
      bands(ctx, f, ['#a884b2', '#916d9c', '#c09ac9', '#9a78a6'], 5);
      break;
    case 'gold_block':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, baseColor);
      fill(ctx, f.x + 4, f.y + 6, f.w - 8, 2, tint(baseColor, 30));
      fill(ctx, f.x + 5, f.y + 18, f.w - 10, 2, tint(baseColor, -20));
      fill(ctx, f.x + 4, f.y + 28, f.w - 8, 2, tint(baseColor, 24));
      break;
    case 'lamp':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#5a422a');
      fill(ctx, f.x + 5, f.y + 5, f.w - 10, f.h - 10, '#b88743');
      fill(ctx, f.x + 8, f.y + 8, f.w - 16, f.h - 16, '#ffd36e');
      fill(ctx, f.x + 2, f.y + 17, f.w - 4, 3, '#5a422a');
      fill(ctx, f.x + 17, f.y + 2, 3, f.h - 4, '#5a422a');
      fill(ctx, f.x + 3, f.y + 3, 3, 3, '#8a6535');
      fill(ctx, f.x + f.w - 6, f.y + 3, 3, 3, '#8a6535');
      fill(ctx, f.x + 3, f.y + f.h - 6, 3, 3, '#8a6535');
      fill(ctx, f.x + f.w - 6, f.y + f.h - 6, 3, 3, '#8a6535');
      break;
    case 'hay':
      bands(ctx, f, ['#dcbf62', '#c6a641', '#efd87a'], 4);
      fill(ctx, f.x + 10, f.y + 3, 2, f.h - 6, '#a57f1f');
      fill(ctx, f.x + 20, f.y + 3, 2, f.h - 6, '#b08725');
      break;
    case 'snow':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#f2f8fd');
      fill(ctx, f.x + 4, f.y + 6, f.w - 8, 2, '#ffffff');
      fill(ctx, f.x + 7, f.y + 18, 10, 2, '#d6e3ee');
      break;
    case 'ice':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#88bce7');
      fill(ctx, f.x + 5, f.y + 4, 14, 4, 'rgba(255,255,255,0.35)');
      fill(ctx, f.x + 9, f.y + 18, 10, 2, 'rgba(220,245,255,0.4)');
      break;
    case 'cactus':
      fill(ctx, f.x + 8, f.y + 2, 14, f.h - 4, '#278a2f');
      fill(ctx, f.x + 10, f.y + 2, 3, f.h - 4, '#5cc25f');
      fill(ctx, f.x + 19, f.y + 2, 3, f.h - 4, '#155d22');
      for (const [x, y] of [[5, 8], [24, 14], [6, 24]]) fill(ctx, f.x + x, f.y + y, 2, 2, '#d7f7bd');
      break;
    case 'kelp':
      fill(ctx, f.x + 12, f.y + 2, 6, f.h - 4, '#2b8d54');
      fill(ctx, f.x + 9, f.y + 8, 3, 6, '#4fc67e');
      fill(ctx, f.x + 18, f.y + 16, 3, 6, '#216a44');
      fill(ctx, f.x + 14, f.y + 5, 2, 4, '#8ce4ae');
      break;
    case 'basalt':
      vStreaks(ctx, f, ['#686d78', '#494d55', '#7e848f', '#585e68'], 4);
      break;
    case 'volcanic':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#2d2a2c');
      noiseGrid(ctx, f, ['#514d4f', '#151314', '#6a6265'], 5, 3);
      break;
    case 'farmland':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#7f4f2c');
      for (let x = f.x + 6; x < f.x + f.w - 3; x += 5) fill(ctx, x, f.y + 3, 2, f.h - 6, '#4b2a17');
      break;
    case 'redstone_wire':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#55585f');
      fill(ctx, f.x + 4, f.y + 16, f.w - 8, 5, '#c93a3a');
      fill(ctx, f.x + 5, f.y + 17, f.w - 10, 3, '#ef5555');
      fill(ctx, f.x + 16, f.y + 4, 5, f.h - 8, '#c93a3a');
      fill(ctx, f.x + 17, f.y + 5, 3, f.h - 10, '#ef5555');
      fill(ctx, f.x + 16, f.y + 16, 5, 5, '#ff6b6b');
      fill(ctx, f.x + 17, f.y + 17, 3, 3, '#ff9a9a');
      fill(ctx, f.x + 6, f.y + 7, 3, 3, '#b03030');
      fill(ctx, f.x + 28, f.y + 7, 3, 3, '#b03030');
      fill(ctx, f.x + 6, f.y + 27, 3, 3, '#b03030');
      fill(ctx, f.x + 28, f.y + 27, 3, 3, '#b03030');
      break;
    case 'lever':
      fill(ctx, f.x + 8, f.y + 26, 22, 7, '#6e7278');
      fill(ctx, f.x + 10, f.y + 27, 18, 5, '#8a8f96');
      fill(ctx, f.x + 22, f.y + 8, 4, 20, '#b08c54');
      fill(ctx, f.x + 23, f.y + 9, 2, 18, '#c9a465');
      fill(ctx, f.x + 21, f.y + 6, 6, 4, '#e8c878');
      fill(ctx, f.x + 22, f.y + 7, 4, 2, '#f2d89a');
      break;
    case 'water':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#3577db');
      bands(ctx, f, ['#5ca6ff', '#3577db', '#2b56a7', '#4a90f0'], 5);
      break;
    case 'lava':
      fill(ctx, f.x + 2, f.y + 2, f.w - 4, f.h - 4, '#dd5e21');
      bands(ctx, f, ['#ffb15b', '#dd5e21', '#8f2814', '#e87030'], 5);
      break;
    case 'sapling':
      fill(ctx, f.x + 10, f.y + 30, 18, 4, '#8c6338');
      fill(ctx, f.x + 12, f.y + 28, 14, 3, '#7a5530');
      fill(ctx, f.x + 17, f.y + 16, 4, 14, '#6a4525');
      fill(ctx, f.x + 18, f.y + 17, 2, 12, '#8c6138');
      fill(ctx, f.x + 12, f.y + 10, 14, 10, '#3aaa40');
      fill(ctx, f.x + 10, f.y + 12, 18, 6, '#2e8a32');
      fill(ctx, f.x + 14, f.y + 8, 10, 4, '#4cc452');
      fill(ctx, f.x + 14, f.y + 10, 4, 3, '#6edd68');
      fill(ctx, f.x + 22, f.y + 14, 3, 2, '#1e6e24');
      break;
    default:
      noiseGrid(ctx, f, [tint(baseColor || '#8a8a8a', 28), baseColor || '#8a8a8a', tint(baseColor || '#8a8a8a', -28)], 5, 3);
      break;
  }
}

/* ── tool handle helper ───────────────────────────────────────────── */

function handle(ctx, x, y, h, w, c) {
  fill(ctx, x, y, w, h, tint(c, -18));
  fill(ctx, x + 1, y + 1, Math.max(1, w - 2), Math.max(1, h - 2), c);
  // subtle wood grain highlight
  fill(ctx, x + 1, y + 2, 1, Math.max(1, h - 4), tint(c, 16));
}

/* ── tool drawing with tier-accurate Minecraft colors ─────────────── */

function drawTool(ctx, toolType, _color, tier = 'wood') {
  const t = TIER[tier] || TIER.wood;
  frame(ctx, t.border);
  const head = t.head;
  const hi = t.hi;
  const lo = t.lo;
  const accent = t.accent;
  const wood = t.handle;
  shadow(ctx, 12, 8, 24, 30, 0.20);

  if (toolType === 'pickaxe') {
    handle(ctx, 21, 15, 22, 6, wood);
    // head bar
    fill(ctx, 10, 10, 28, 6, lo);
    fill(ctx, 12, 12, 24, 3, head);
    // downward pointed tips
    fill(ctx, 8, 13, 8, 5, head);
    fill(ctx, 32, 13, 8, 5, head);
    // top highlight
    fill(ctx, 12, 12, 24, 1, hi);
    // tier accent
    fill(ctx, 12, 11, 24, 1, accent);
    return;
  }
  if (toolType === 'axe') {
    handle(ctx, 21, 16, 21, 6, wood);
    // axe head – right side blade
    fill(ctx, 18, 8, 18, 10, lo);
    fill(ctx, 20, 9, 14, 8, head);
    fill(ctx, 20, 9, 14, 2, hi);
    // blade edge extends down-right
    fill(ctx, 32, 12, 6, 8, lo);
    fill(ctx, 33, 13, 4, 6, head);
    fill(ctx, 33, 13, 4, 1, hi);
    // tier accent
    fill(ctx, 20, 9, 14, 1, accent);
    return;
  }
  if (toolType === 'shovel') {
    handle(ctx, 21, 18, 19, 6, wood);
    // shovel blade – rounded spade shape
    fill(ctx, 16, 6, 16, 14, lo);
    fill(ctx, 18, 7, 12, 12, head);
    fill(ctx, 18, 7, 12, 2, hi);
    // rounded bottom tip
    fill(ctx, 20, 18, 8, 3, lo);
    fill(ctx, 22, 20, 4, 2, lo);
    fill(ctx, 20, 18, 8, 1, head);
    // tier accent
    fill(ctx, 18, 7, 12, 1, accent);
    return;
  }
  if (toolType === 'hoe') {
    handle(ctx, 21, 16, 21, 6, wood);
    // hoe head – L-shaped blade
    fill(ctx, 18, 8, 18, 6, lo);
    fill(ctx, 20, 9, 14, 4, head);
    fill(ctx, 20, 9, 14, 1, hi);
    // blade extension down
    fill(ctx, 30, 14, 6, 4, lo);
    fill(ctx, 31, 14, 4, 3, head);
    // tier accent
    fill(ctx, 20, 9, 14, 1, accent);
    return;
  }
  if (toolType === 'sword') {
    // blade – vertical, pointed top, centered at x=24
    // blade body
    fill(ctx, 19, 6, 10, 4, lo);
    fill(ctx, 20, 7, 8, 3, head);
    fill(ctx, 20, 7, 8, 1, hi);
    // blade tip
    fill(ctx, 21, 4, 6, 3, hi);
    fill(ctx, 22, 3, 4, 2, accent);
    // blade shaft
    fill(ctx, 20, 10, 8, 16, lo);
    fill(ctx, 21, 11, 6, 14, head);
    fill(ctx, 22, 12, 2, 12, hi);
    // guard
    fill(ctx, 14, 26, 20, 3, tint(head, 10));
    fill(ctx, 15, 27, 18, 1, tint(head, 24));
    // handle
    handle(ctx, 21, 29, 9, 6, wood);
    // pommel
    fill(ctx, 20, 38, 8, 3, lo);
    fill(ctx, 21, 38, 6, 2, head);
    // tier accent
    fill(ctx, 22, 3, 4, 1, accent);
    fill(ctx, 22, 12, 2, 12, accent);
    return;
  }
  if (toolType === 'bow') {
    // bow limb (curved left side)
    fill(ctx, 10, 8, 4, 30, '#7B4F24');
    fill(ctx, 11, 9, 3, 28, '#A06830');
    fill(ctx, 14, 12, 3, 24, '#B87840');
    // limb highlight
    fill(ctx, 11, 10, 1, 26, '#C09050');
    // string (right side)
    fill(ctx, 30, 8, 2, 30, '#E8E0D0');
    // string-to-limb connectors
    fill(ctx, 14, 8, 16, 2, '#A06830');
    fill(ctx, 14, 36, 16, 2, '#A06830');
    // arrow rest
    fill(ctx, 18, 22, 8, 3, '#D0B870');
    return;
  }
  if (toolType === 'shield') {
    // shield shape – pointed bottom, flat top
    poly(ctx, [[14, 6], [34, 6], [35, 12], [35, 26], [24, 38], [13, 26], [13, 12]], lo);
    poly(ctx, [[16, 8], [32, 8], [33, 12], [33, 24], [24, 35], [15, 24], [15, 12]], head);
    // iron border detail
    fill(ctx, 16, 8, 16, 2, hi);
    // center cross
    fill(ctx, 22, 12, 4, 18, tint(head, 24));
    fill(ctx, 18, 18, 12, 4, tint(head, 24));
    // center accent
    fill(ctx, 23, 19, 2, 2, '#DDD0A8');
    return;
  }
  if (toolType === 'flint_steel') {
    // iron striker (right side, curved piece)
    fill(ctx, 24, 10, 14, 6, '#AAB0BA');
    fill(ctx, 26, 8, 10, 3, '#C0C6D0');
    fill(ctx, 24, 16, 6, 12, '#98A0AC');
    fill(ctx, 26, 12, 3, 3, '#D4D8E0');
    // flint (left side, dark angular stone)
    fill(ctx, 10, 16, 10, 16, '#3E4450');
    fill(ctx, 12, 14, 6, 3, '#4A5060');
    fill(ctx, 12, 18, 6, 10, '#586068');
    fill(ctx, 14, 20, 3, 4, '#6E7680');
    // sparks
    fill(ctx, 22, 8, 2, 2, '#FFCC44');
    fill(ctx, 19, 12, 2, 2, '#FFE87A');
    fill(ctx, 25, 6, 2, 2, '#FF9920');
    return;
  }
  if (toolType === 'shears') {
    // left blade
    fill(ctx, 8, 5, 4, 18, '#BCC2CC');
    fill(ctx, 12, 9, 4, 14, '#C8CED8');
    fill(ctx, 9, 6, 2, 14, '#D8DCE4');
    // right blade
    fill(ctx, 36, 5, 4, 18, '#A4AAB4');
    fill(ctx, 32, 9, 4, 14, '#B4BAC4');
    fill(ctx, 37, 6, 2, 14, '#969EA8');
    // pivot
    fill(ctx, 20, 21, 8, 4, '#8A9098');
    fill(ctx, 22, 22, 4, 2, '#C0C6CE');
    // left grip
    fill(ctx, 14, 27, 8, 12, '#7A6A50');
    fill(ctx, 16, 29, 4, 8, '#4A3E30');
    // right grip
    fill(ctx, 26, 27, 8, 12, '#7A6A50');
    fill(ctx, 28, 29, 4, 8, '#4A3E30');
    return;
  }
  if (toolType === 'watercraft') {
    const boatColor = _color || '#A77A4F';
    fill(ctx, 8, 24, 32, 8, tint(boatColor, -24));
    fill(ctx, 10, 22, 28, 6, boatColor);
    fill(ctx, 14, 18, 20, 4, tint(boatColor, 18));
    fill(ctx, 22, 13, 4, 7, tint(boatColor, 28));
    return;
  }
  // fallback generic tool
  handle(ctx, 21, 16, 21, 6, wood);
  fill(ctx, 17, 8, 14, 8, lo);
  fill(ctx, 19, 9, 10, 6, head);
}

/* ── bucket drawing ───────────────────────────────────────────────── */

function drawBucket(ctx, itemId = 'bucket') {
  frame(ctx, TIER.iron.border);
  const d = 5;
  const m = '#B4BCC8', mL = tint(m, 34), mD = tint(m, -34), mS = tint(m, -62);
  shadow(ctx, 10, 5 + d, 28, 32, 0.22);
  // handle arch
  fill(ctx, 12, 8 + d, 3, 10, mD);
  fill(ctx, 33, 8 + d, 3, 10, mD);
  fill(ctx, 15, 5 + d, 18, 3, mL);
  fill(ctx, 15, 8 + d, 18, 2, tint(m, -8));
  // rim
  fill(ctx, 12, 14 + d, 24, 3, mD);
  // body
  fill(ctx, 10, 17 + d, 28, 14, mD);
  fill(ctx, 13, 17 + d, 22, 14, m);
  fill(ctx, 13, 17 + d, 3, 14, mL);
  fill(ctx, 32, 17 + d, 3, 14, mS);
  // bottom
  fill(ctx, 11, 31 + d, 26, 3, mS);
  // inner void
  fill(ctx, 16, 17 + d, 16, 12, '#111418');
  fill(ctx, 16, 28 + d, 16, 2, '#0a0c10');

  if (itemId === 'water_bucket') {
    fill(ctx, 16, 17 + d, 16, 3, '#8ED0FF');
    fill(ctx, 16, 20 + d, 16, 8, '#3F84DD');
    fill(ctx, 18, 22 + d, 3, 3, '#B8E8FF');
    fill(ctx, 27, 24 + d, 2, 3, '#5CA3F2');
  } else if (itemId === 'lava_bucket') {
    fill(ctx, 16, 17 + d, 16, 3, '#FFD07E');
    fill(ctx, 16, 20 + d, 16, 8, '#F06722');
    fill(ctx, 18, 24 + d, 3, 2, '#FFE19A');
    fill(ctx, 25, 22 + d, 3, 3, '#FF8E2F');
    fill(ctx, 16, 28 + d, 16, 2, '#CF3F16');
  }
}

/* ── material shapes (centered, Minecraft-faithful) ───────────────── */

function drawMaterialShape(ctx, color, shape = 'gem') {
  frame(ctx, color);
  const base = color || '#93B8FF';
  const light = tint(base, 38), dk = tint(base, -34);
  shadow(ctx, 12, 10, 24, 24, 0.16);

  if (shape === 'coal') {
    // irregular dark chunk – centered
    fill(ctx, 15, 15, 18, 16, dk);
    fill(ctx, 17, 13, 14, 2, dk);
    fill(ctx, 13, 19, 4, 8, dk);
    fill(ctx, 17, 17, 12, 12, base);
    fill(ctx, 19, 19, 4, 4, light);
    fill(ctx, 25, 22, 3, 3, tint(base, 18));
    return;
  }
  if (shape === 'bone') {
    // horizontal bone – laid flat for easy recognition
    // left knobs (top & bottom)
    fill(ctx, 5, 16, 5, 6, '#E8E4DC');
    fill(ctx, 5, 26, 5, 6, '#E8E4DC');
    fill(ctx, 4, 17, 3, 4, '#F4F0EA');
    fill(ctx, 4, 27, 3, 4, '#F4F0EA');
    // shaft (horizontal)
    fill(ctx, 10, 20, 28, 8, '#DDD8D0');
    fill(ctx, 11, 21, 26, 6, '#ECE8E2');
    fill(ctx, 13, 22, 14, 3, '#F6F2EE');
    // right knobs (top & bottom)
    fill(ctx, 38, 16, 5, 6, '#E8E4DC');
    fill(ctx, 38, 26, 5, 6, '#E8E4DC');
    fill(ctx, 41, 17, 3, 4, '#F4F0EA');
    fill(ctx, 41, 27, 3, 4, '#F4F0EA');
    return;
  }
  if (shape === 'stick') {
    // centered stick
    fill(ctx, 21, 8, 6, 30, tint('#B88557', -20));
    fill(ctx, 22, 9, 4, 28, '#B88557');
    fill(ctx, 23, 10, 1, 26, tint('#B88557', 20));
    return;
  }
  if (shape === 'string') {
    // wavy string centered
    for (const [x, off] of [[13, 0], [19, -3], [25, 0], [31, -3]])
      fill(ctx, x, 11 + off, 2, 26, '#E6E0D4');
    fill(ctx, 14, 12, 5, 2, '#F2ECE2');
    fill(ctx, 26, 12, 5, 2, '#F2ECE2');
    return;
  }
  if (shape === 'arrow') {
    // horizontal arrow centered
    // shaft
    fill(ctx, 9, 22, 28, 3, '#C6B69A');
    fill(ctx, 10, 23, 26, 1, '#D6C8AF');
    // point (left)
    fill(ctx, 5, 19, 6, 9, '#8F8F8F');
    fill(ctx, 6, 20, 4, 7, '#A0A0A0');
    fill(ctx, 4, 22, 2, 3, '#808080');
    // fletching (right)
    fill(ctx, 34, 18, 6, 4, '#D9CFBD');
    fill(ctx, 34, 25, 6, 4, '#D9CFBD');
    fill(ctx, 35, 19, 4, 3, '#E8E0D0');
    fill(ctx, 35, 25, 4, 3, '#E8E0D0');
    return;
  }
  if (shape === 'ingot') {
    // Minecraft-style ingot – centered trapezoid
    // shadow base
    fill(ctx, 10, 20, 28, 10, dk);
    // main body
    fill(ctx, 12, 21, 24, 8, base);
    // top face (lighter, angled)
    fill(ctx, 14, 17, 20, 5, light);
    fill(ctx, 16, 18, 16, 3, tint(base, 22));
    // highlight
    fill(ctx, 14, 17, 20, 1, tint(light, 16));
    fill(ctx, 14, 22, 6, 3, tint(base, 12));
    return;
  }
  if (shape === 'torch') {
    // centered torch
    fill(ctx, 22, 16, 4, 22, '#9F6A3A');
    fill(ctx, 23, 17, 2, 20, '#B88050');
    // flame
    fill(ctx, 20, 8, 8, 10, '#F5B049');
    fill(ctx, 22, 10, 4, 7, '#FFE085');
    fill(ctx, 23, 9, 2, 3, '#FFF0B0');
    // glow
    fill(ctx, 19, 12, 1, 3, '#E89030');
    fill(ctx, 28, 12, 1, 3, '#E89030');
    return;
  }
  if (shape === 'powder') {
    // scattered powder piles – centered
    fill(ctx, 14, 20, 7, 5, dk);
    fill(ctx, 23, 16, 7, 5, base);
    fill(ctx, 17, 26, 9, 5, light);
    fill(ctx, 28, 25, 5, 4, dk);
    fill(ctx, 11, 15, 4, 4, tint(base, -12));
    return;
  }
  if (shape === 'flint') {
    // angular dark stone – centered
    poly(ctx, [[22, 8], [32, 14], [30, 34], [17, 30], [14, 16]], dk);
    poly(ctx, [[23, 11], [29, 16], [27, 30], [19, 27], [17, 17]], base);
    fill(ctx, 22, 16, 4, 6, light);
    return;
  }
  if (shape === 'feather') {
    // centered quill feather
    fill(ctx, 18, 8, 12, 24, '#F2EEE7');
    fill(ctx, 23, 6, 2, 32, '#CFC6B8');
    fill(ctx, 16, 12, 3, 4, '#FFFFFF');
    fill(ctx, 28, 18, 3, 4, '#E0DAD0');
    fill(ctx, 23, 36, 2, 4, '#A09888');
    return;
  }
  if (shape === 'leather') {
    // piece of leather – centered
    fill(ctx, 12, 13, 24, 20, '#8E653D');
    fill(ctx, 14, 15, 20, 16, '#AA7A49');
    fill(ctx, 17, 18, 8, 4, '#C8955F');
    fill(ctx, 26, 24, 5, 3, '#9A6A38');
    return;
  }
  if (shape === 'wool') {
    // fluffy wool ball – centered
    fill(ctx, 10, 13, 28, 22, '#ECE7DF');
    fill(ctx, 13, 15, 22, 18, '#F6F1EA');
    fill(ctx, 17, 14, 5, 3, '#DAD3C8');
    fill(ctx, 26, 20, 5, 4, '#DAD3C8');
    fill(ctx, 14, 26, 4, 3, '#D4CDC2');
    return;
  }
  if (shape === 'eye') {
    // Eye of Ender – centered green with purple pupil
    fill(ctx, 13, 14, 22, 18, '#79BF58');
    fill(ctx, 15, 16, 18, 14, '#A0D890');
    fill(ctx, 18, 19, 12, 10, '#D8EAD0');
    fill(ctx, 21, 21, 6, 6, '#7A4BD8');
    fill(ctx, 22, 22, 4, 4, '#9060E8');
    fill(ctx, 23, 23, 2, 2, '#B080FF');
    return;
  }
  if (shape === 'seed') {
    // wheat seeds – centered small cluster
    fill(ctx, 16, 16, 5, 10, '#C7B067');
    fill(ctx, 23, 14, 5, 10, '#C7B067');
    fill(ctx, 19, 20, 5, 8, '#A89048');
    // small sprout
    fill(ctx, 26, 18, 2, 8, '#8CB44B');
    fill(ctx, 25, 16, 3, 3, '#517A2E');
    return;
  }
  if (shape === 'wheat') {
    // wheat stalk – centered
    fill(ctx, 22, 10, 4, 28, '#9C7741');
    fill(ctx, 23, 11, 2, 26, '#B08C50');
    // grain heads on alternating sides
    for (const [x, y] of [[15, 12], [27, 14], [14, 18], [28, 20], [15, 26], [27, 28]])
      fill(ctx, x, y, 6, 3, '#D8BE62');
    fill(ctx, 20, 8, 8, 3, '#E8D070');
    return;
  }
  if (shape === 'boat') {
    // boat – centered
    fill(ctx, 8, 24, 32, 8, '#8F6235');
    fill(ctx, 10, 22, 28, 6, '#A97844');
    fill(ctx, 14, 18, 20, 4, '#CB995E');
    fill(ctx, 12, 22, 2, 6, '#7A5028');
    fill(ctx, 34, 22, 2, 6, '#7A5028');
    return;
  }
  if (shape === 'raft') {
    // bamboo raft – centered
    fill(ctx, 8, 22, 32, 6, '#CCB067');
    fill(ctx, 10, 28, 28, 3, '#94793D');
    fill(ctx, 22, 14, 4, 8, '#A8833B');
    fill(ctx, 8, 22, 2, 6, '#B8A058');
    fill(ctx, 38, 22, 2, 6, '#B8A058');
    return;
  }
  // gem default – Minecraft diamond shape (hexagonal, centered)
  // top facet
  fill(ctx, 20, 10, 8, 3, tint(light, 10));
  // upper facets
  fill(ctx, 16, 13, 16, 3, light);
  // middle body
  fill(ctx, 13, 16, 22, 4, base);
  // lower facets
  fill(ctx, 15, 20, 18, 3, dk);
  fill(ctx, 17, 23, 14, 3, dk);
  // bottom point
  fill(ctx, 19, 26, 10, 2, tint(dk, -10));
  fill(ctx, 21, 28, 6, 2, tint(dk, -18));
  fill(ctx, 23, 30, 2, 2, tint(dk, -26));
  // top highlight sparkle
  fill(ctx, 21, 11, 3, 2, '#FFFFFF');
  fill(ctx, 17, 14, 3, 2, tint(light, 20));
}

/* ── food shapes (Minecraft-style) ────────────────────────────────── */

function drawFoodShape(ctx, itemId, color) {
  frame(ctx, color);
  const base = color || '#C9924D';
  const light = tint(base, 28), dk = tint(base, -28);
  shadow(ctx, 12, 12, 24, 22, 0.18);

  if (itemId === 'apple') {
    // Minecraft apple – round red body, centered
    fill(ctx, 16, 17, 16, 2, dk);
    fill(ctx, 14, 19, 20, 12, dk);
    fill(ctx, 16, 31, 16, 2, dk);
    fill(ctx, 16, 19, 16, 10, base);
    fill(ctx, 15, 21, 18, 6, base);
    // highlight
    fill(ctx, 17, 20, 6, 4, light);
    fill(ctx, 18, 19, 3, 2, tint(light, 14));
    // indent
    fill(ctx, 22, 17, 4, 2, tint(base, -14));
    // stem
    fill(ctx, 23, 11, 3, 7, '#6E4724');
    fill(ctx, 24, 12, 1, 5, '#8B5C30');
    // leaf
    fill(ctx, 26, 10, 7, 4, '#69B24F');
    fill(ctx, 27, 9, 5, 2, '#84DD6A');
    return;
  }
  if (itemId === 'berry' || itemId === 'blueberry') {
    // berry cluster – 3 round berries, centered
    // berry 1 (top-left)
    fill(ctx, 13, 18, 10, 8, dk);
    fill(ctx, 14, 19, 8, 6, base);
    fill(ctx, 15, 20, 3, 2, light);
    // berry 2 (top-right)
    fill(ctx, 25, 16, 10, 8, dk);
    fill(ctx, 26, 17, 8, 6, tint(base, -8));
    fill(ctx, 27, 18, 3, 2, base);
    // berry 3 (bottom-center)
    fill(ctx, 18, 27, 10, 8, dk);
    fill(ctx, 19, 28, 8, 6, tint(base, 8));
    fill(ctx, 20, 29, 3, 2, light);
    // stems
    fill(ctx, 21, 11, 3, 7, '#67B14F');
    fill(ctx, 18, 10, 4, 2, '#8AD870');
    fill(ctx, 25, 11, 4, 2, '#4E9B40');
    return;
  }
  if (itemId === 'bread') {
    // long baguette-style loaf – horizontal, centered
    fill(ctx, 6, 19, 36, 12, dk);
    fill(ctx, 8, 20, 32, 10, base);
    // rounded crusty top
    fill(ctx, 10, 17, 28, 4, light);
    fill(ctx, 14, 16, 20, 2, tint(light, 12));
    // scoring slash marks
    for (const x of [14, 21, 28]) fill(ctx, x, 21, 2, 7, tint(base, -18));
    return;
  }
  if (itemId === 'aloe') {
    // aloe vera – thick fleshy leaves centered
    // center leaf
    fill(ctx, 21, 10, 6, 22, '#4E8C54');
    fill(ctx, 22, 12, 4, 18, '#5DB868');
    fill(ctx, 23, 14, 2, 6, '#8CE4A0');
    // left leaves
    poly(ctx, [[10, 28], [14, 14], [18, 16], [14, 30]], '#3D7A48');
    poly(ctx, [[12, 26], [15, 16], [17, 18], [14, 28]], '#58A866');
    // right leaves
    poly(ctx, [[38, 28], [34, 14], [30, 16], [34, 30]], '#3D7A48');
    poly(ctx, [[36, 26], [33, 16], [31, 18], [34, 28]], '#58A866');
    // outer leaves
    poly(ctx, [[6, 32], [10, 20], [14, 22], [10, 34]], '#347042');
    poly(ctx, [[42, 32], [38, 20], [34, 22], [38, 34]], '#347042');
    // base
    fill(ctx, 16, 32, 16, 4, '#3A6640');
    return;
  }
  if (itemId === 'painkiller') {
    // pill capsule – centered
    fill(ctx, 13, 19, 22, 10, '#DFE5EC');
    fill(ctx, 13, 19, 11, 10, base);
    fill(ctx, 24, 19, 11, 10, '#F4F7FB');
    // rounded ends
    fill(ctx, 14, 18, 9, 2, tint(base, 8));
    fill(ctx, 25, 18, 9, 2, '#F8FAFC');
    fill(ctx, 14, 29, 9, 1, tint(base, -14));
    fill(ctx, 25, 29, 9, 1, '#D0D8E0');
    // highlight
    fill(ctx, 16, 20, 4, 2, light);
    fill(ctx, 28, 20, 4, 2, '#FFFFFF');
    return;
  }
  if (itemId === 'medkit') {
    // med kit – centered white box with red cross
    fill(ctx, 12, 14, 24, 20, base);
    fill(ctx, 13, 15, 22, 18, tint(base, 10));
    // white cross
    fill(ctx, 20, 18, 8, 3, '#FFFFFF');
    fill(ctx, 22, 15, 4, 9, '#FFFFFF');
    fill(ctx, 20, 26, 8, 3, '#FFFFFF');
    fill(ctx, 22, 24, 4, 8, '#FFFFFF');
    // latch
    fill(ctx, 22, 14, 4, 2, tint(base, -20));
    return;
  }
  // generic meat shapes (raw / cooked)
  const isCooked = itemId.endsWith('_cooked');
  const meatBase = isCooked ? mix(base, '#6B3B26', 0.35) : base;
  const fat = isCooked ? '#D0A374' : '#E4C7B4';
  const mDk = tint(meatBase, -22);
  // meat slab – centered
  fill(ctx, 11, 16, 26, 16, mDk);
  fill(ctx, 13, 18, 22, 12, meatBase);
  fill(ctx, 16, 20, 8, 3, fat);
  fill(ctx, 26, 24, 5, 3, tint(meatBase, 20));
}

/* ── armor shapes with tier-based Minecraft colors ────────────────── */

function drawArmorShape(ctx, _color, slot, tier = 'leather') {
  const t = TIER[tier] || TIER.leather;
  frame(ctx, t.border);
  const base = t.head;
  const light = t.hi;
  const dk = t.lo;
  shadow(ctx, 14, 8, 20, 30, 0.16);

  if (slot === 'helmet') {
    // dome shape – centered
    fill(ctx, 16, 8, 16, 3, dk);
    fill(ctx, 14, 11, 20, 12, dk);
    fill(ctx, 16, 12, 16, 10, base);
    // top highlight
    fill(ctx, 18, 10, 12, 2, light);
    fill(ctx, 20, 9, 8, 2, tint(light, 10));
    // visor slit
    fill(ctx, 16, 20, 16, 3, tint(dk, -20));
    fill(ctx, 18, 21, 12, 1, '#1A1D26');
    // ear guards
    fill(ctx, 13, 22, 4, 7, dk);
    fill(ctx, 14, 23, 2, 5, base);
    fill(ctx, 31, 22, 4, 7, dk);
    fill(ctx, 32, 23, 2, 5, base);
    // tier-specific trim
    fill(ctx, 16, 23, 16, 1, t.accent);
    return;
  }
  if (slot === 'chestplate') {
    // torso shape – centered
    fill(ctx, 14, 8, 20, 28, dk);
    fill(ctx, 16, 10, 16, 24, base);
    // shoulders
    fill(ctx, 9, 8, 7, 10, dk);
    fill(ctx, 10, 9, 5, 8, base);
    fill(ctx, 32, 8, 7, 10, dk);
    fill(ctx, 33, 9, 5, 8, base);
    // collar
    fill(ctx, 18, 10, 12, 3, light);
    fill(ctx, 20, 9, 8, 2, tint(light, 10));
    // center seam
    fill(ctx, 23, 16, 2, 16, tint(base, -10));
    // tier accent trim
    fill(ctx, 14, 35, 20, 1, t.accent);
    fill(ctx, 9, 17, 7, 1, t.accent);
    fill(ctx, 32, 17, 7, 1, t.accent);
    return;
  }
  if (slot === 'leggings') {
    // waistband
    fill(ctx, 14, 8, 20, 6, dk);
    fill(ctx, 16, 9, 16, 4, base);
    fill(ctx, 18, 9, 12, 2, light);
    // left leg
    fill(ctx, 14, 14, 9, 22, dk);
    fill(ctx, 16, 15, 5, 20, base);
    fill(ctx, 17, 16, 2, 16, tint(base, 8));
    // right leg
    fill(ctx, 25, 14, 9, 22, dk);
    fill(ctx, 27, 15, 5, 20, base);
    fill(ctx, 28, 16, 2, 16, tint(base, 8));
    // belt accent
    fill(ctx, 16, 9, 16, 1, t.accent);
    return;
  }
  if (slot === 'boots') {
    // left boot – centered pair
    fill(ctx, 10, 14, 12, 20, dk);
    fill(ctx, 12, 16, 8, 12, base);
    fill(ctx, 13, 17, 4, 6, light);
    fill(ctx, 9, 30, 14, 5, dk);
    fill(ctx, 10, 31, 12, 3, tint(base, -6));
    // right boot
    fill(ctx, 26, 14, 12, 20, dk);
    fill(ctx, 28, 16, 8, 12, base);
    fill(ctx, 29, 17, 4, 6, light);
    fill(ctx, 25, 30, 14, 5, dk);
    fill(ctx, 26, 31, 12, 3, tint(base, -6));
    // tier accent at top
    fill(ctx, 10, 14, 12, 1, t.accent);
    fill(ctx, 26, 14, 12, 1, t.accent);
    return;
  }
  // fallback
  fill(ctx, 15, 12, 18, 24, dk);
  fill(ctx, 17, 14, 14, 20, base);
  fill(ctx, 19, 16, 10, 4, light);
}

/* ── visual key mapping ───────────────────────────────────────────── */

function getBlockVisualKey(def) {
  const n = String(def?.name || '').toLowerCase();
  if (n.includes('grass')) return 'grass';
  if (n.includes('dirt')) return 'dirt';
  if (n.includes('leaves')) return 'leaves';
  if (n.includes('log')) return 'log';
  if (n.includes('planks')) return 'planks';
  if (n.includes('iron ore') || n.includes('diamond ore') || n.includes('gold ore') || n.includes('coal ore')) return 'ore';
  if (n.includes('crafting table')) return 'crafting_table';
  if (n.includes('furnace')) return 'furnace';
  if (n.includes('chest')) return 'chest';
  if (n.includes('bed')) return 'bed';
  if (n.includes('door') && !n.includes('trap')) return 'door';
  if (n.includes('trapdoor')) return 'trapdoor';
  if (n.includes('fence')) return 'fence';
  if (n.includes('sandstone')) return 'sandstone';
  if (n.includes('glass')) return 'glass';
  if (n.includes('concrete')) return 'concrete';
  if (n.includes('asphalt')) return 'asphalt';
  if (n.includes('sand')) return 'sand';
  if (n.includes('gravel')) return 'gravel';
  if (n.includes('mossy cobble') || n.includes('cobblestone')) return 'cobble';
  if (n.includes('stone bricks') || n.includes('chiseled stone') || n.includes('cracked stone') || n.includes('red brick') || n.includes('bricks')) return 'stone_bricks';
  if (n.includes('smooth stone')) return 'smooth_stone';
  if (n.includes('obsidian')) return 'obsidian';
  if (n.includes('glowstone')) return 'glowstone';
  if (n.includes('quartz')) return 'quartz';
  if (n.includes('netherrack')) return 'netherrack';
  if (n.includes('soul sand')) return 'soul_sand';
  if (n.includes('nether bricks')) return 'nether_bricks';
  if (n.includes('end stone')) return 'end_stone';
  if (n.includes('purpur')) return 'purpur';
  if (n.includes('gold block') || n.includes('iron block') || n.includes('diamond block') || n.includes('coal block')) return 'gold_block';
  if (n.includes('redstone lamp')) return 'lamp';
  if (n.includes('hay')) return 'hay';
  if (n.includes('snow')) return 'snow';
  if (n.includes('ice')) return 'ice';
  if (n.includes('cactus')) return 'cactus';
  if (n.includes('kelp')) return 'kelp';
  if (n.includes('basalt')) return 'basalt';
  if (n.includes('volcanic')) return 'volcanic';
  if (n.includes('farmland')) return 'farmland';
  if (n.includes('redstone wire')) return 'redstone_wire';
  if (n.includes('lever')) return 'lever';
  if (n.includes('sapling')) return 'sapling';
  if (n.includes('water')) return 'water';
  if (n.includes('lava')) return 'lava';
  if (n.includes('stone')) return 'stone';
  return 'generic';
}

function getMaterialShape(id) {
  if (id === 'coal' || id === 'charcoal') return 'coal';
  if (id === 'gunpowder' || id === 'bone_meal') return 'powder';
  if (id === 'stick') return 'stick';
  if (id === 'bone') return 'bone';
  if (id === 'string') return 'string';
  if (id === 'arrow') return 'arrow';
  if (id === 'iron_ingot' || id === 'gold_ingot') return 'ingot';
  if (id === 'torch') return 'torch';
  if (id === 'flint') return 'flint';
  if (id === 'feather') return 'feather';
  if (id === 'leather') return 'leather';
  if (id === 'wool') return 'wool';
  if (id === 'ender_eye') return 'eye';
  if (id === 'wheat_seed') return 'seed';
  if (id === 'wheat') return 'wheat';
  if (id === 'boat') return 'boat';
  if (id === 'bamboo_raft') return 'raft';
  return 'gem';
}

/* ── slot background helper (exported) ─────────────────────────────── */

export function dimColor(hex, factor = 0.35) {
  const c = String(hex || '#888').replace('#', '');
  const n = c.length === 3
    ? c.split('').map((x) => x + x).join('')
    : c.padEnd(6, '8').slice(0, 6);
  const v = Number.parseInt(n, 16);
  const r = Math.round(((v >> 16) & 255) * factor);
  const g = Math.round(((v >> 8) & 255) * factor);
  const b = Math.round((v & 255) * factor);
  return `rgb(${r},${g},${b})`;
}

/* ── armor tier detection ─────────────────────────────────────────── */

function getArmorTier(itemId) {
  if (itemId.startsWith('diamond_')) return 'diamond';
  if (itemId.startsWith('iron_')) return 'iron';
  if (itemId.startsWith('gold_')) return 'gold';
  return 'leather';
}

/* ── public API ────────────────────────────────────────────────────── */

export function createItemIconProvider(itemDefs) {
  const cache = new Map();

  function build(itemId) {
    const def = itemDefs[itemId];
    if (!def) return '';

    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    clr(ctx);

    if (itemId === 'bucket' || itemId === 'water_bucket' || itemId === 'lava_bucket') {
      drawBucket(ctx, itemId);
    } else if (itemId === 'torch') {
      drawMaterialShape(ctx, def.color, 'torch');
    } else if (def.kind === 'tool') {
      drawTool(ctx, def.toolType, def.color, def.tier);
    } else if (def.kind === 'armor') {
      drawArmorShape(ctx, def.color, def.armorSlot, getArmorTier(itemId));
    } else if (def.kind === 'food') {
      drawFoodShape(ctx, itemId, def.color);
    } else if (def.kind === 'material') {
      drawMaterialShape(ctx, def.color, getMaterialShape(itemId));
    } else if (def.kind === 'block') {
      drawBlockVariant(ctx, getBlockVisualKey(def), def.color);
    } else {
      drawBlockVariant(ctx, 'generic', def.color);
    }

    return canvas.toDataURL('image/png');
  }

  return {
    getIcon(itemId) {
      if (!itemId) return '';
      if (!cache.has(itemId)) cache.set(itemId, build(itemId));
      return cache.get(itemId);
    }
  };
}
